"use client";
import type { WhisperJson } from "@teksta/shared-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { hasWordTimings } from "subtitletools";
import { getWordsFromSegment } from "@/lib/subs/utils";
import { cn } from "@/lib/utils";
import { getHasWordTimestamps } from "../whisper-json";

interface TranscriptViewerProps {
  words: SegmentWord[];
  currentTime: number;
  maxLines?: number;
  transitionThreshold?: number;
  futureWordThreshold?: number;
  targetLineDuration?: number;
  maxCharsPerLine?: number;
  lineHeightPx?: number;
  wordAnimations?: boolean;
}

const TranscriptViewer: React.FC<TranscriptViewerProps> = ({
  words,
  currentTime,
  maxLines = 2,
  transitionThreshold = 0,
  futureWordThreshold = 50,
  targetLineDuration = 4000,
  maxCharsPerLine = 40,
  lineHeightPx = 24,
  wordAnimations = true,
}) => {
  /**
   * 1. Precompute lines in a single pass
   */
  const allLines = useMemo(() => {
    const newLines: SegmentWord[][] = [];
    let currentLine: SegmentWord[] = [];
    let currentLineChars = 0;
    let lineStartTime = 0;

    words.forEach((word, idx) => {
      if (currentLine.length === 0) {
        // Start a new line
        currentLine.push(word);
        currentLineChars = word.text.length;
        lineStartTime = word.startTime;
        return;
      }

      const wordChars = word.text.length;
      const wordDuration = word.endTime - lineStartTime;
      const currentLineDuration =
        currentLine[currentLine.length - 1].endTime - currentLine[0].startTime;

      const exceedsDuration = currentLineDuration + wordDuration > targetLineDuration;
      const exceedsChars = maxCharsPerLine > 0 && currentLineChars + wordChars > maxCharsPerLine;

      if (exceedsDuration || exceedsChars) {
        newLines.push(currentLine);
        currentLine = [word];
        currentLineChars = wordChars;
        lineStartTime = word.startTime;
      } else {
        currentLine.push(word);
        currentLineChars += wordChars;
      }

      // If we reached the last word, push whatever is in currentLine
      if (idx === words.length - 1 && currentLine.length > 0) {
        newLines.push(currentLine);
      }
    });

    return newLines;
  }, [words, maxCharsPerLine, targetLineDuration]);

  /**
   * 2. Find the "active" line index:
   *    We'll show a line if:
   *    - currentTime + futureWordThreshold >= lineStart (appear early),
   *    - currentTime <= lineEnd + transitionThreshold (stay a bit after end).
   */
  const currentLineIndex = useMemo(() => {
    for (let i = 0; i < allLines.length; i++) {
      const line = allLines[i];
      const lineStart = line[0].startTime;
      const lineEnd = line[line.length - 1].endTime;

      const appearsEarly = currentTime + 2000 >= lineStart;
      const staysLate = currentTime <= lineEnd + transitionThreshold;

      if (appearsEarly && staysLate) {
        return i;
      }
    }
    return -1;
  }, [allLines, currentTime, futureWordThreshold, transitionThreshold]);

  /**
   * 3. Slice out the lines we want to display (up to maxLines).
   */
  const linesToShow = useMemo(() => {
    if (allLines.length === 0) return [];

    if (currentLineIndex === -1) {
      // If we're before the first line's start, show from start
      if (currentTime < (allLines[0]?.[0].startTime ?? 0)) {
        return allLines.slice(0, maxLines);
      } else {
        // Otherwise, show up to maxLines from the end
        return allLines.slice(-maxLines);
      }
    }

    // Show up to maxLines lines, ending at currentLineIndex
    const startIndex = Math.max(0, currentLineIndex - (maxLines - 1));
    const endIndex = currentLineIndex + 1;
    return allLines.slice(startIndex, endIndex);
  }, [allLines, currentLineIndex, maxLines, currentTime]);

  /**
   * 4. Reverse linesToShow so newest line is index=0, next line is 1, etc.
   */
  const reversedLinesToShow = useMemo(() => {
    return [...linesToShow].reverse();
  }, [linesToShow]);

  /**
   * 5. Keep a "renderLines" state with phases: "entering", "stable", "leaving"
   */
  type Phase = "entering" | "stable" | "leaving";
  interface RenderLine {
    id: string;
    words: SegmentWord[];
    index: number; // 0 => bottom, 1 => above, etc.
    phase: Phase;
  }

  const [renderLines, setRenderLines] = useState<RenderLine[]>([]);

  /**
   * 6. Merge lines whenever reversedLinesToShow changes.
   *    - brand new => "entering"
   *    - existing => keep old phase
   *    - removed => "leaving"
   */
  useEffect(() => {
    const updated: RenderLine[] = reversedLinesToShow.map((line, i) => {
      const firstWord = line[0];
      const lastWord = line[line.length - 1];
      const id = `${firstWord.startTime}_${lastWord.endTime}`;

      return {
        id,
        words: line,
        index: i, // 0 is bottom, 1 is above, etc.
        phase: "stable" as Phase, // temporary; might become entering if new
      };
    });

    setRenderLines((prev) => {
      const prevMap = new Map(prev.map((rl) => [rl.id, rl]));
      const next: RenderLine[] = [];

      // Mark lines that remain or are new
      updated.forEach((u) => {
        const oldItem = prevMap.get(u.id);
        if (oldItem) {
          // keep old phase if not "leaving"
          // if was "leaving", resurrect it
          const newPhase = oldItem.phase === "leaving" ? "entering" : oldItem.phase;
          next.push({
            ...oldItem,
            index: u.index,
            phase: newPhase,
            words: u.words,
          });
        } else {
          // brand new => entering
          next.push({ ...u, phase: "entering" });
        }
      });

      // Mark lines that disappeared => "leaving"
      prev.forEach((oldItem) => {
        if (!updated.find((u) => u.id === oldItem.id)) {
          // if it's not already leaving, set leaving
          if (oldItem.phase !== "leaving") {
            next.push({ ...oldItem, phase: "leaving" });
          } else {
            // already leaving, keep
            next.push(oldItem);
          }
        }
      });

      // Sort by index asc, but push leaving lines last so they appear behind
      next.sort((a, b) => {
        if (a.phase === "leaving" && b.phase !== "leaving") return 1;
        if (b.phase === "leaving" && a.phase !== "leaving") return -1;
        return a.index - b.index;
      });
      return next;
    });
  }, [reversedLinesToShow]);

  /**
   * 7. Animate "entering" lines => "stable" after 2 RAFs
   */
  useEffect(() => {
    const anyEntering = renderLines.some((rl) => rl.phase === "entering");
    if (anyEntering) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setRenderLines((lines) =>
            lines.map((rl) => (rl.phase === "entering" ? { ...rl, phase: "stable" } : rl))
          );
        });
      });
    }
  }, [renderLines]);

  /**
   * 8. Render lines.
   *    - entering => slide up from bottom
   *    - stable => no transform
   *    - leaving => slide up further and fade out
   *
   *    On transition end, if a line is "leaving", remove it from state.
   */
  return (
    <div
      // Media-screen surface: deliberately black with white captions in both
      // themes (inherited foreground was unreadable in light mode).
      className="relative w-full overflow-hidden rounded-lg border border-white/10 bg-black px-3 py-2 text-white"
      style={{ height: `${maxLines * lineHeightPx * 2}px` }}
    >
      {renderLines.map(({ id, words, index, phase }) => {
        const bottomPx = 8 + index * lineHeightPx; // small spacing

        let transformClass = "translate-y-0";
        let opacityClass = "opacity-100";

        if (phase === "entering") {
          transformClass = "translate-y-full"; // slide up from below
        } else if (phase === "leaving") {
          transformClass = "-translate-y-full"; // slide further up
          opacityClass = "opacity-0"; // fade out
        }

        return (
          <div
            key={id}
            className={`
                absolute w-full leading-tight
                transition-[transform,opacity] duration-500 ease-in-out
                transform ${transformClass} ${opacityClass}
              `}
            style={{
              bottom: `${bottomPx}px`,
            }}
            onTransitionEnd={(e) => {
              // if a line finishes leaving, remove it from state
              if (phase === "leaving" && e.currentTarget === e.target) {
                setRenderLines((prev) => prev.filter((r) => r.id !== id));
              }
            }}
          >
            {(wordAnimations &&
              words.map((word, wIdx) => {
                const isActive = currentTime >= word.startTime && currentTime <= word.endTime;
                const isFuture = currentTime < word.startTime;

                return (
                  <span
                    key={wIdx}
                    className={`transition-opacity duration-200 ${
                      isActive ? "font-semibold" : ""
                    } ${isFuture ? "opacity-50" : "opacity-100"}`}
                  >
                    {word.text}{" "}
                  </span>
                );
              })) ||
              words.map((word, wIdx) => {
                return <span key={wIdx}>{word.text} </span>;
              })}
          </div>
        );
      })}
    </div>
  );
};

export const TranscriptAudioPlayer = React.forwardRef<
  HTMLAudioElement,
  React.HTMLAttributes<HTMLDivElement> & {
    src: string;
    whisperJson: WhisperJson;
  }
>(({ src, whisperJson, className, ...props }, audioRef) => {
  const [currentTime, setCurrentTime] = useState(0);
  const innerAudioRef = useRef<HTMLAudioElement>(null);

  const hasWordTimestamps = getHasWordTimestamps(whisperJson);
  const words = getSegmentWordsFromWhisperJson({ whisperJson });

  // Use the forwarded ref if provided, otherwise use inner ref
  //const audioRef = (ref as React.RefObject<HTMLAudioElement>) || innerAudioRef;

  useEffect(() => {
    if (!audioRef || typeof audioRef === "function") return;
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime * 1000);

    audio.addEventListener("timeupdate", updateTime);
    return () => audio.removeEventListener("timeupdate", updateTime);
  }, [audioRef]);

  return (
    <div {...props} className={cn("w-full", className)}>
      <audio ref={audioRef} controls src={src} className="w-full" />
      <TranscriptViewer
        words={words}
        currentTime={currentTime}
        wordAnimations={hasWordTimestamps}
        futureWordThreshold={hasWordTimestamps ? 500 : 100}
      />
    </div>
  );
});
TranscriptAudioPlayer.displayName = "TranscriptAudioPlayer";

export const TranscriptAudioPlayerPodcast = ({
  src,
  whisperJson,
  audioId,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  src: string;
  whisperJson: WhisperJson;
  audioId: string;
}) => {
  const [currentTime, setCurrentTime] = useState(0);
  const innerAudioRef = useRef<HTMLAudioElement>(null);

  const hasWordTimestamps = getHasWordTimestamps(whisperJson);
  const words = getSegmentWordsFromWhisperJson({ whisperJson });

  useEffect(() => {
    const audio = innerAudioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime * 1000);

    audio.addEventListener("timeupdate", updateTime);
    return () => audio.removeEventListener("timeupdate", updateTime);
  }, [innerAudioRef.current]);

  return (
    <div {...props} className="w-full">
      <audio ref={innerAudioRef} controls src={src} className="w-full" id={audioId} />
      <TranscriptViewer
        words={words}
        currentTime={currentTime}
        wordAnimations={hasWordTimestamps}
        futureWordThreshold={hasWordTimestamps ? 500 : 100}
      />
    </div>
  );
};

const getSegmentWordsFromWhisperJson = ({
  whisperJson,
}: {
  whisperJson: WhisperJson;
}): SegmentWord[] => {
  const words: SegmentWord[] = [];
  if (hasWordTimings(whisperJson.segments)) {
    whisperJson.segments.forEach((segment) => {
      segment.words.forEach((word) => {
        words.push({
          text: word.word,
          startTime: word.start * 1000,
          endTime: word.end * 1000,
        });
      });
    });
  } else {
    whisperJson.segments.forEach((segment) => {
      const _words = getWordsFromSegment({
        text: segment.text,
        start: segment.start * 1000,
        end: segment.end * 1000,
      });
      words.push(..._words);
    });
  }
  return words;
};
